import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Circle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Pinned live-article banner. Renders NULL when no live article is pinned,
 * so this can safely mount anywhere on the site without leaving empty space.
 */
export default function PinnedLiveBanner() {
  const [item, setItem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get("/live-articles/pinned");
        if (!cancelled) setItem(data.item || null);
      } catch {
        if (!cancelled) setItem(null);
      }
    }
    load();
    // Refresh every 60s so a newly pinned article shows up without a hard reload.
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!item) return null;
  const isLive = item.status === "live";
  const latest = (item.updates && item.updates[0]) || null;

  return (
    <Link
      to={`/live/${item.id}`}
      data-testid="pinned-live-banner"
      className="block w-full bg-gradient-to-r from-titans-red via-titans-red to-[#8f0a20] hover:from-titans-red-hover hover:to-titans-red text-white transition-colors"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-3 flex items-center gap-4">
        <span className="inline-flex items-center gap-1.5 font-cond uppercase tracking-[0.3em] text-[10px] shrink-0">
          {isLive ? (
            <>
              <Circle size={8} className="fill-white animate-pulse" />
              LIVE
            </>
          ) : (
            <>
              <Radio size={10} /> Just Ended
            </>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm md:text-base uppercase tracking-wide truncate">
            {item.title}
          </div>
          {latest && (
            <div className="text-white/85 text-xs md:text-sm font-body truncate">
              {latest.text}
            </div>
          )}
        </div>
        <ArrowRight size={16} className="shrink-0 hidden sm:block" />
      </div>
    </Link>
  );
}
